import { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router';
import { api } from '@/api/client';
import { legacyHref } from './nav';
import s from './NotificationView.module.css';

type Item = { id: string; type: string; title: string; body?: string | null; orderId?: string | null; actionUrl?: string | null; readAt?: string | null; createdAt: string };
type Payload = { items: Item[]; unreadCount: number; totalCount: number };
type Filter = 'all' | 'review' | 'change' | 'stock' | 'shipped';
type Undo = { ids: string[]; readAt: string; expiresAt: number };
const READONLY = import.meta.env.VITE_READONLY !== '0';

function groupOf(type: string): Filter {
  if (type.startsWith('ORDER_CHANGE_')) return 'change';
  if (type.startsWith('ORDER_STOCK_') || type.startsWith('PURCHASE_')) return 'stock';
  if (type === 'ORDER_SHIPPED' || type.startsWith('SHIPMENT_')) return 'shipped';
  return 'review';
}

function displayTitle(item: Item) {
  if (item.type === 'ORDER_REVIEW_REQUIRED') return '新订单待审核';
  if (item.type === 'ORDER_CHANGE_REVIEW_REQUIRED') return '变更待审核';
  return item.title;
}

function dayOf(value: string) {
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? '' : new Intl.DateTimeFormat('en-CA', { timeZone: 'Asia/Shanghai', year: 'numeric', month: '2-digit', day: '2-digit' }).format(date);
}

function timeOf(value: string) {
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? '' : new Intl.DateTimeFormat('zh-CN', { timeZone: 'Asia/Shanghai', hour: '2-digit', minute: '2-digit', hour12: false }).format(date);
}

function destination(item: Item): { to?: string; href?: string } {
  if (item.orderId) return { to: `/orders/${encodeURIComponent(item.orderId)}` };
  const path = item.actionUrl;
  if (!path || !path.startsWith('/') || path.startsWith('//') || path.startsWith('/\\')) return {};
  if (/^\/orders\/[^/]+$/.test(path)) return { to: path };
  return { href: legacyHref(path) };
}

export function NotificationView({ onClose, onUnreadChange, tabletPopover = false }: { onClose?: () => void; onUnreadChange?: (count: number) => void; tabletPopover?: boolean }) {
  const navigate = useNavigate();
  const [payload, setPayload] = useState<Payload | null>(null);
  const [filter, setFilter] = useState<Filter>('all');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [undo, setUndo] = useState<Undo | null>(null);
  const [swipedId, setSwipedId] = useState<string | null>(null);
  const touchStart = useRef<{ id: string; x: number; y: number } | null>(null);
  const load = useCallback(async (signal?: AbortSignal) => {
    setLoading(true); setError('');
    try {
      const result = await api.get<Payload>('/api/v2/notifications', undefined, signal);
      if (signal?.aborted) return;
      setPayload(result);
      onUnreadChange?.(result.unreadCount);
    } catch (cause) { if (!signal?.aborted) setError(cause instanceof Error ? cause.message : '通知加载失败'); }
    finally { if (!signal?.aborted) setLoading(false); }
  }, [onUnreadChange]);
  useEffect(() => { const controller = new AbortController(); void load(controller.signal); return () => controller.abort(); }, [load]);
  useEffect(() => {
    const onNew = () => { void load(); };
    window.addEventListener('ims:v2:notification', onNew);
    return () => window.removeEventListener('ims:v2:notification', onNew);
  }, [load]);
  useEffect(() => {
    if (!undo) return;
    const timer = window.setTimeout(() => setUndo(null), Math.max(0, undo.expiresAt - Date.now()));
    return () => window.clearTimeout(timer);
  }, [undo]);

  const markRead = async (item: Item) => {
    if (READONLY || item.readAt) return;
    await api.patch(`/api/v2/notifications/${encodeURIComponent(item.id)}/read`, {});
    setPayload((current) => current ? { ...current, unreadCount: Math.max(0, current.unreadCount - 1), items: current.items.map((row) => row.id === item.id ? { ...row, readAt: new Date().toISOString() } : row) } : current);
    onUnreadChange?.(Math.max(0, (payload?.unreadCount || 0) - 1));
  };
  const open = async (item: Item) => {
    try { await markRead(item); } catch (cause) { setMessage(cause instanceof Error ? cause.message : '标记已读失败'); }
    const target = destination(item);
    if (target.to) { onClose?.(); navigate(target.to); }
    else if (target.href) window.location.assign(target.href);
  };
  const readAll = async () => {
    if (READONLY || !payload?.unreadCount) return;
    try {
      const result = await api.patch<{ ids: string[]; readAt: string }>('/api/v2/notifications/read-all', {});
      const ids = new Set(result.ids);
      setPayload((current) => current ? { ...current, unreadCount: Math.max(0, current.unreadCount - result.ids.length), items: current.items.map((item) => ids.has(item.id) ? { ...item, readAt: result.readAt } : item) } : current);
      onUnreadChange?.(Math.max(0, payload.unreadCount - result.ids.length));
      setUndo({ ...result, expiresAt: Date.now() + 5000 });
      setMessage(`已将 ${result.ids.length} 条设为已读`);
    } catch (cause) { setMessage(cause instanceof Error ? cause.message : '操作失败'); }
  };
  const undoReadAll = async () => {
    if (!undo) return;
    try {
      await api.post('/api/v2/notifications/undo-read-all', { ids: undo.ids, readAt: undo.readAt });
      setUndo(null);
      setMessage('已撤销');
      void load();
    } catch (cause) { setMessage(cause instanceof Error ? cause.message : '撤销失败'); }
  };
  const remove = async (item: Item) => {
    if (READONLY) return;
    try {
      await api.delete(`/api/v2/notifications/${encodeURIComponent(item.id)}`);
      setPayload((current) => current ? {
        ...current,
        totalCount: Math.max(0, current.totalCount - 1),
        unreadCount: Math.max(0, current.unreadCount - (item.readAt ? 0 : 1)),
        items: current.items.filter((row) => row.id !== item.id),
      } : current);
      onUnreadChange?.(Math.max(0, (payload?.unreadCount || 0) - (item.readAt ? 0 : 1)));
      setSwipedId(null);
      setMessage('通知已删除');
    } catch (cause) { setMessage(cause instanceof Error ? cause.message : '删除通知失败'); }
  };

  const counts: Record<Filter, number> = { all: payload?.unreadCount || 0, review: 0, change: 0, stock: 0, shipped: 0 };
  payload?.items.forEach((item) => { if (!item.readAt) counts[groupOf(item.type)] += 1; });
  const visible = (payload?.items || []).filter((item) => filter === 'all' || groupOf(item.type) === filter);
  const today = dayOf(new Date().toISOString());
  let lastDay = '';

  return <div className={`${s.view} ${tabletPopover ? s.tabletPopover : ''}`}>
    <header className={`${s.header} ${tabletPopover ? s.tabletHeader : ''}`}>
      {tabletPopover ? <>
        <h1>通知 <small className={s.unreadCount}>{payload?.unreadCount || 0} 条未读</small></h1>
        <button type="button" className={s.headerReadAll} disabled={READONLY || !payload?.unreadCount} onClick={() => void readAll()}>全部已读</button>
      </> : <>
        {onClose && <button type="button" className={s.back} onClick={onClose}>‹ 主屏</button>}
        <h1>通知</h1>
      </>}
      <Link to="/me/notifications" onClick={onClose} aria-label="通知设置">⚙</Link>
    </header>
    <div className={`${s.filters} ${tabletPopover ? s.tabletPopoverFilters : ''}`} role="tablist" aria-label="通知类型">{([
      ['all', '全部'], ['review', '待审核'], ['change', '变更'], ['stock', '库存'], ['shipped', '发货'],
    ] as [Filter, string][]).map(([key, label]) => <button key={key} type="button" role="tab" aria-selected={filter === key} className={filter === key ? s.active : ''} onClick={() => setFilter(key)}>{label} <small>{counts[key]}</small></button>)}</div>
    {!tabletPopover && <div className={s.summary}><span>{payload?.unreadCount || 0} 条未读 · 点开哪条读哪条{READONLY ? ' · 当前只读' : ''}</span><button type="button" disabled={READONLY || !payload?.unreadCount} onClick={() => void readAll()}>全部已读</button></div>}
    <div className={s.list}>
      {loading && !payload && <p className={s.state}>加载中…</p>}
      {error && <p className={s.state} role="alert">{error} <button type="button" onClick={() => void load()}>重试</button></p>}
      {!loading && !error && !visible.length && <p className={s.state}>暂无通知</p>}
      {visible.map((item) => {
        const day = dayOf(item.createdAt);
        const showDay = day !== lastDay;
        lastDay = day;
        const sticky = item.type === 'ORDER_STOCK_RECOVERED';
        return <div key={item.id}>
          {showDay && <div className={s.day}>{day === today ? '今天' : day} <span>{day === today ? '' : '保留 30 天'}</span></div>}
          <article className={`${s.item} ${item.readAt ? s.read : ''} ${swipedId === item.id ? s.swiped : ''}`} onTouchStart={(event) => { touchStart.current = { id: item.id, x: event.touches[0].clientX, y: event.touches[0].clientY }; }} onTouchEnd={(event) => { const start = touchStart.current; touchStart.current = null; if (!start || start.id !== item.id) return; const dx = event.changedTouches[0].clientX - start.x; const dy = event.changedTouches[0].clientY - start.y; if (Math.abs(dx) > 45 && Math.abs(dx) > Math.abs(dy) * 1.5) setSwipedId(dx < 0 ? item.id : null); }}>
            {!READONLY && <button type="button" className={s.delete} aria-label={`删除通知：${item.title}`} onClick={() => void remove(item)}>删除</button>}
            <div className={s.itemContent}>
            <button type="button" className={s.itemMain} onClick={() => { if (swipedId === item.id) { setSwipedId(null); return; } void open(item); }}>
              <span className={s.unreadMark}>{!item.readAt && <i />}</span>
              <span className={`${s.itemIcon} ${s[`icon_${groupOf(item.type)}`]}`} aria-hidden="true">{groupOf(item.type) === 'change' ? '✎' : groupOf(item.type) === 'stock' ? '▧' : groupOf(item.type) === 'shipped' ? '▰' : '✓'}</span>
              <span className={s.itemText}><span className={s.itemTitle}><b>{displayTitle(item)}</b><time dateTime={item.createdAt}>{timeOf(item.createdAt)}</time></span>
                {item.body && <span className={s.body}>{item.body}</span>}
              </span>
            </button>
            {sticky && <div className={s.itemActions}><button type="button" onClick={() => setMessage('已稍后处理；通知仍保留在列表中')}>稍后处理</button><button type="button" onClick={() => void open(item)}>查看订单</button></div>}
            </div>
          </article>
        </div>;
      })}
      {payload && payload.totalCount > payload.items.length && <p className={s.state}>仅显示最近 {payload.items.length} 条</p>}
    </div>
    {(message || undo) && <div className={s.toast} role="status"><span>{message}</span>{undo && <button type="button" onClick={() => void undoReadAll()}>撤销</button>}</div>}
  </div>;
}
